import Vue from 'vue';
import InternalChatAPI from '../../api/internalChat';
import { FEATURE_FLAGS } from '../../featureFlags';
import {
  getUserPermissions,
  hasPermissions,
  userCanReadResource,
} from '../../helper/permissionsHelper';

// Chat interno (#1748). O número que importa aqui é o de NÃO LIDAS — o badge da sidebar —, e
// ele chega por dois caminhos que não respeitam ordem entre si: a resposta HTTP (lista de
// canais, marcação de leitura) e o socket (mensagem nova, leitura feita em outra aba). Uma
// resposta pode ter saído do servidor antes de um evento e chegar depois dele, ou o contrário.
//
// Por isso as não lidas NÃO são um contador que se soma e se zera. São uma função de:
//
//   * a FOTO mais recente do servidor para o canal — `unread_count`, `last_read_message_id` e
//     `last_message_id`, lidos num comando SQL só (InternalChat::UnreadCounter#snapshot), então
//     coerentes entre si;
//   * as mensagens que o socket trouxe com id ACIMA da foto (as de baixo ela já contou);
//   * até onde esta aba sabe que leu (marcação otimista, e mensagem própria: enviar avança o
//     marcador no servidor).
//
// Foto velha que chega depois de uma nova é descartada (os dois ids só andam para a frente), e
// mensagem repetida é a mesma mensagem (deduplicada por id). Com isso a ordem de chegada deixa
// de importar — ver specs/internalChat/unread.spec.js.
//
// Grupos (#1749): um canal `kind: 'group'` entra no mesmo mapa `channels` e conta não lidas do
// mesmo jeito. O que muda é a saída: grupo desfeito (`archived_at`) ou de que a pessoa foi
// removida sai do store, e o id vai para `removedChannelIds` — uma mensagem enviada antes da
// saída, que chegue pelo socket depois dela, não pode recriar o grupo como esboço.
//
// Administradores e regras (#1764): o canal do grupo traz `admin_ids` e `settings` (as três
// regras), da lista e do `internal_chat.channel_updated`. É deles que a tela tira quem vê as ações
// de gestão e se o compositor fica travado — quem decide de verdade é o servidor.
//
// Editar e apagar (#1757): o evento `internal_chat.message_updated` traz a mensagem como ficou.
// Ele também não respeita ordem — pode chegar antes do `message_created` da mesma mensagem, ou
// depois de uma foto que já não a conta. Por isso toda mudança fica guardada em `updates`, por
// canal e por id, e vale sobre qualquer cópia que chegue depois (histórico, socket, prévia da
// lista): apagada vence sempre, e entre duas edições vence a de `revision` maior.
//
// A apagada some das não lidas pelos dois caminhos:
//   * a do socket deixa de ser contada (`fromSocket` ignora `deleted_at`);
//   * a que só existia na foto é descontada da foto — se a foto a contou. A foto traz
//     `deletion_mark`, o instante da exclusão mais recente que ela já descontou, e a exclusão
//     traz `revision`, o instante dela (os dois do relógio do banco, crescentes na ordem de
//     commit dentro do canal). Exclusão com `revision` acima da marca é posterior à foto: a
//     foto a contou, e ela sai. Abaixo ou igual, a foto já não a contava.
//
// Arquivos (#1756): a mensagem traz `attachments`. Os LINKS de cada arquivo (`url`,
// `download_url`, `thumb_url`) são de quem pediu pelo HTTP e vencem (`urls_expire_at`); o evento do
// socket traz o arquivo sem link. Duas cópias da mesma mensagem, então, podem diferir só nos
// links — e a que vence pela regra acima (a mais nova) herda os links da outra quando não tem os
// seus, para o `message_created` do socket não apagar da tela a imagem que a resposta do envio
// já mostrou. Mensagem apagada não tem arquivo nenhum, nem nome, em lugar nenhum do store.
//
// Visão consolidada (#1752): o store guarda canais de MAIS DE UMA conta. Os ids de canal são do
// banco, únicos entre contas, e cada canal leva `account_id`. Três regras sustentam o resto:
//
//   * a conta de toda chamada sobre um canal é a do CANAL (`accountOf`), nunca a da URL — é
//     assim que a resposta numa conversa de outra conta vai para a conta certa;
//   * conta CONHECIDA é a da URL ou uma que o servidor listou em `GET /internal_chat/accounts`
//     (`accounts`). Evento, carga ou resposta de conta desconhecida é descartado; conta que sai
//     da lista leva junto tudo o que era dela;
//   * as não lidas continuam por canal, com a foto de cada um. Conta ainda não carregada usa o
//     número do agregado até a lista dela chegar — e só se carrega de saída a conta que tem
//     não lidas (o resto, quando a pessoa a abre ou quando chega evento dela).
//
// Responder e encaminhar (#1775): a mensagem traz `reply_to` — a CITAÇÃO da original, lida pelo
// servidor na hora em que serializou — e `forwarded`. A citação não é cópia congelada: quando a
// original muda (`message_updated`), toda resposta carregada que a cita é atualizada junto, e a
// cópia mais nova da citação vence pela mesma régua das mensagens (apagada sempre; senão a de
// `revision` maior; no empate, a resolvida vence a pendente). "Respondendo a" do compositor fica
// em `replyingTo`, por canal: o envio seguinte daquele canal sai como resposta e limpa o estado.
//
// Menção a quem lê (#1784): o "@" ao lado do contador. Segue a mesma régua das não lidas — foto
// mais o que o socket trouxe acima dela, menos o que esta aba já leu. A foto traz `mention_ids`, as
// não lidas que citavam a pessoa naquele instante; a mensagem conhecida (histórico, socket,
// mudança) decide por si, pela versão mais nova: `mentioned_user_ids` no evento do socket (que
// não leva nome de menção) e a menção `user` resolvida na resposta HTTP. Assim a menção apagada
// ou tirada na edição apaga o "@", e a que chega pelo socket o acende na hora.

export const types = {
  RESET: 'INTERNAL_CHAT/RESET',
  SET_UI_FLAG: 'INTERNAL_CHAT/SET_UI_FLAG',
  SET_CHANNELS: 'INTERNAL_CHAT/SET_CHANNELS',
  UPSERT_CHANNEL: 'INTERNAL_CHAT/UPSERT_CHANNEL',
  ADD_CHANNEL_STUB: 'INTERNAL_CHAT/ADD_CHANNEL_STUB',
  ADD_MESSAGES: 'INTERNAL_CHAT/ADD_MESSAGES',
  SET_HAS_MORE: 'INTERNAL_CHAT/SET_HAS_MORE',
  SET_HISTORY_LOADED: 'INTERNAL_CHAT/SET_HISTORY_LOADED',
  SET_LOCAL_READ: 'INTERNAL_CHAT/SET_LOCAL_READ',
  UNDO_LOCAL_READ: 'INTERNAL_CHAT/UNDO_LOCAL_READ',
  APPLY_SNAPSHOT: 'INTERNAL_CHAT/APPLY_SNAPSHOT',
  SET_ACTIVE_CHANNEL: 'INTERNAL_CHAT/SET_ACTIVE_CHANNEL',
  APPLY_CHANNEL_INFO: 'INTERNAL_CHAT/APPLY_CHANNEL_INFO',
  REMOVE_CHANNEL: 'INTERNAL_CHAT/REMOVE_CHANNEL',
  APPLY_MESSAGE_UPDATE: 'INTERNAL_CHAT/APPLY_MESSAGE_UPDATE',
  SET_REPLYING_TO: 'INTERNAL_CHAT/SET_REPLYING_TO',
  SET_ACCOUNTS: 'INTERNAL_CHAT/SET_ACCOUNTS',
  REMOVE_ACCOUNT: 'INTERNAL_CHAT/REMOVE_ACCOUNT',
  SET_PEOPLE: 'INTERNAL_CHAT/SET_PEOPLE',
};

const MAX_CATCH_UP_PAGES = 5;

// Fora do estado: não é dado de tela, e o Vuex não deve observar um AbortController.
let pendingUpload = null;
const refreshing = new Map();
// A resposta do agregado que vale é a do pedido MAIS NOVO (#1752): uma lista velha que chega
// atrasada devolveria uma conta que acabou de sair.
let accountsRequest = 0;
// Uma carga de lista por conta de cada vez: a rajada de eventos de uma conta ainda não carregada
// pede a lista dela uma vez só.
const loadingAccounts = new Map();

export const blankState = () => ({
  // Contas cuja lista de canais já chegou: { [accountId]: true } (#1752).
  loadedAccounts: {},
  // As contas da visão consolidada, como o servidor as listou: [{ id, name, unread_count,
  // mention_count }]. É a ÚNICA fonte de "outra conta conhecida" — a lista branca do socket
  // (actionCable.js) consulta `isCrossAccountEligible`, que lê daqui.
  accounts: [],
  accountsLoaded: false,
  // As pessoas das OUTRAS contas: { [accountId]: [pessoa] }. A da URL vem do store `agents`.
  people: {},
  channels: {},
  messages: {},
  hasMore: {},
  historyLoaded: {},
  removedChannelIds: {},
  // { [channelId]: { [messageId]: mensagem editada/apagada } } — ver o cabeçalho.
  updates: {},
  // { [channelId]: citação } — a mensagem que o compositor daquele canal está respondendo (#1775).
  replyingTo: {},
  activeChannelId: null,
  uiFlags: {
    isFetchingChannels: false,
    isFetchingMessages: false,
    isSending: false,
    isSavingGroup: false,
    // 0–100 durante o envio de arquivos; null fora dele (#1756).
    uploadProgress: null,
  },
});

const toId = value => Number(value) || 0;

const snapshotOf = source => ({
  unread_count: toId(source.unread_count),
  last_read_message_id: toId(source.last_read_message_id),
  last_message_id: toId(source.last_message_id),
  deletion_mark: toId(source.deletion_mark),
  mention_ids: (source.mention_ids || []).map(toId).filter(Boolean),
});

// Foto nova substitui a anterior só se não for mais velha. Os dois ids só crescem no servidor
// (a mensagem apagada continua valendo para `last_message_id`, e o marcador é monotônico),
// então "mais velha" é ter visto menos mensagens, ou as mesmas com o marcador mais atrás. Com
// os dois empatados, desempata a exclusão mais recente que a foto viu (#1757): sem isso, a
// foto de antes de uma exclusão, chegando atrasada, venceria o empate e voltaria a contar a
// mensagem apagada.
export const isNotOlderSnapshot = (current, incoming) => {
  if (!current) return true;
  if (incoming.last_message_id !== current.last_message_id) {
    return incoming.last_message_id > current.last_message_id;
  }
  if (incoming.last_read_message_id !== current.last_read_message_id) {
    return incoming.last_read_message_id > current.last_read_message_id;
  }
  return (incoming.deletion_mark || 0) >= (current.deletion_mark || 0);
};

const LINK_FIELDS = ['url', 'download_url', 'thumb_url', 'urls_expire_at'];

// Os links mais novos de um arquivo, entre duas cópias dele (#1756).
const freshestLinks = (attachment, other) => {
  if (!other?.url) return attachment;
  if (
    attachment.url &&
    (attachment.urls_expire_at || 0) >= (other.urls_expire_at || 0)
  ) {
    return attachment;
  }
  const links = {};
  LINK_FIELDS.forEach(field => {
    links[field] = other[field] ?? null;
  });
  return { ...attachment, ...links };
};

// A versão que venceu fica com os links mais novos que as duas conhecem. Devolve o MESMO objeto
// quando não há o que trocar.
const withFreshestLinks = (winner, other) => {
  if (!other || winner === other || winner.deleted_at) return winner;
  const known = new Map(
    (other.attachments || []).map(attachment => [attachment.id, attachment])
  );
  let changed = false;
  const attachments = (winner.attachments || []).map(attachment => {
    const merged = freshestLinks(attachment, known.get(attachment.id));
    if (merged !== attachment) changed = true;
    return merged;
  });
  return changed ? { ...winner, attachments } : winner;
};

// Menções (#1750): a versão do socket chega com `mentions_pending` — o evento vai para todos os
// membros, então não leva nome de menção nenhuma — e a do HTTP chega resolvida para quem pediu.
// Entre duas cópias da MESMA revisão, a pendente nunca apaga a resolvida: o `message_created` do
// socket que chega depois da resposta do envio não pode trocar os chips por "restritos".
const withResolvedMentions = (winner, other) => {
  if (!other || winner === other || winner.deleted_at) return winner;
  if (!winner.mentions_pending || other.mentions_pending) return winner;
  if ((other.revision || 0) !== (winner.revision || 0)) return winner;
  return {
    ...winner,
    content: other.content,
    mentions: other.mentions || [],
    mentions_pending: false,
  };
};

// Das duas cópias da citação da MESMA original (#1775), a mais nova: apagada vence sempre, depois a
// de `revision` maior, e no empate a resolvida vence a pendente — o socket não leva nome de
// menção, a resposta HTTP leva.
export const newerQuote = (current, incoming) => {
  if (!current) return incoming || null;
  if (!incoming || current === incoming || current.id !== incoming.id) {
    return current;
  }
  if (current.deleted !== incoming.deleted) {
    return current.deleted ? current : incoming;
  }
  const currentRevision = current.revision || 0;
  const incomingRevision = incoming.revision || 0;
  if (currentRevision !== incomingRevision) {
    return incomingRevision > currentRevision ? incoming : current;
  }
  return current.mentions_pending && !incoming.mentions_pending
    ? incoming
    : current;
};

// A vencedora fica com a citação mais nova das duas cópias.
const withNewestQuote = (winner, other) => {
  if (!other || winner === other || !winner.reply_to || !other.reply_to) {
    return winner;
  }
  const quote = newerQuote(winner.reply_to, other.reply_to);
  return quote === winner.reply_to ? winner : { ...winner, reply_to: quote };
};

// Das duas versões da mesma mensagem, a mais recente (#1757). Apagada vence sempre — nada
// "desapaga" —, e entre duas que não estão apagadas vence a de `revision` maior (0 = nunca
// editada). No empate fica a que chegou agora, que é a forma completa.
export const newerVersion = (current, incoming) => {
  if (!current) return incoming;
  if (!incoming) return current;
  if (current.deleted_at && !incoming.deleted_at) return current;
  if (incoming.deleted_at && !current.deleted_at) return incoming;
  return (incoming.revision || 0) >= (current.revision || 0)
    ? withNewestQuote(
        withResolvedMentions(withFreshestLinks(incoming, current), current),
        current
      )
    : withNewestQuote(
        withResolvedMentions(withFreshestLinks(current, incoming), incoming),
        incoming
      );
};

const maxId = (messages, predicate = () => true) =>
  messages.reduce(
    (max, message) =>
      predicate(message) && message.id > max ? message.id : max,
    0
  );

// As exclusões conhecidas de um canal: as que vieram pelo evento (`updates`) e as que o
// histórico trouxe já apagadas. Uma por id.
const deletionsOf = (messages, updates) => {
  const byId = new Map();
  messages.forEach(message => {
    if (message.deleted_at) byId.set(message.id, message);
  });
  Object.values(updates || {}).forEach(message => {
    if (message.deleted_at) byId.set(message.id, message);
  });
  return [...byId.values()];
};

// Quantas não lidas a foto contou e já não existem: apagadas DEPOIS da foto (`revision` acima
// de `deletion_mark`), de outra pessoa, dentro do intervalo que a foto contava.
const deletedSinceSnapshot = (snapshot, deletions, userId) =>
  deletions.filter(
    message =>
      message.id > snapshot.last_read_message_id &&
      message.id <= snapshot.last_message_id &&
      message.sender_id !== userId &&
      (message.revision || 0) > snapshot.deletion_mark
  ).length;

// Até onde `userId` leu o canal: a foto, a marcação otimista desta aba e a própria mensagem
// (enviar avança o marcador no servidor).
const lastReadOf = (channel, snapshot, messages, userId) =>
  Math.max(
    snapshot.last_read_message_id,
    channel.local_read_id || 0,
    maxId(messages, message => message.sender_id === userId)
  );

// Não lidas de UM canal para `userId`. Pura — é o coração do badge, e o spec a exercita nas
// intercalações de foto e evento. `updates` são as mudanças (#1757) recebidas para o canal.
export const unreadOf = (channel, knownMessages, userId, updates) => {
  if (!channel) return 0;
  const messages = knownMessages || [];
  const snapshot = channel.snapshot || snapshotOf({});
  const highWater = snapshot.last_message_id;
  const lastRead = lastReadOf(channel, snapshot, messages, userId);
  // Quem leu até o fim do que a foto viu não deve nada a ela. Leitura PARCIAL depois da foto
  // (até uma mensagem do meio) não é descontada: a tela sempre marca até a última.
  const fromSnapshot =
    lastRead >= highWater
      ? 0
      : Math.max(
          0,
          snapshot.unread_count -
            deletedSinceSnapshot(
              snapshot,
              deletionsOf(messages, updates),
              userId
            )
        );
  const fromSocket = messages.filter(
    message =>
      message.id > highWater &&
      message.id > lastRead &&
      message.sender_id !== userId &&
      !message.deleted_at
  ).length;
  return fromSnapshot + fromSocket;
};

// A mensagem cita `userId`? Pelo evento do socket (`mentioned_user_ids`, só os ids citados que
// são membros) ou pela resposta HTTP (a menção `user` resolvida para quem lê). Apagada não cita,
// e a própria mensagem não conta.
export const mentionsUser = (message, userId) =>
  !!message &&
  !!userId &&
  !message.deleted_at &&
  message.sender_id !== userId &&
  ((message.mentioned_user_ids || []).includes(userId) ||
    (message.mentions || []).some(
      entry => entry?.type === 'user' && Number(entry.id) === userId
    ));

// O "@" de UM canal (#1784): há não lida que cita `userId`? Pura, como `unreadOf`. A versão mais
// nova que esta aba conhece de cada mensagem decide; a que só a foto conhece vale pela foto.
export const hasUnreadMentionOf = (channel, knownMessages, userId, updates) => {
  if (!channel || !userId) return false;
  const messages = knownMessages || [];
  const snapshot = channel.snapshot || snapshotOf({});
  const lastRead = lastReadOf(channel, snapshot, messages, userId);
  const latest = new Map(messages.map(message => [message.id, message]));
  Object.values(updates || {}).forEach(update => {
    latest.set(update.id, newerVersion(latest.get(update.id), update));
  });
  const fromKnown = [...latest.values()].some(
    message => message.id > lastRead && mentionsUser(message, userId)
  );
  return (
    fromKnown ||
    snapshot.mention_ids.some(id => id > lastRead && !latest.has(id))
  );
};

const lastKnownId = (channel, messages = []) =>
  Math.max(
    channel?.snapshot?.last_message_id || 0,
    channel?.last_message?.id || 0,
    maxId(messages)
  );

export const isInternalChatAvailable = (rootGetters, accountId) =>
  !!accountId &&
  !!rootGetters['accounts/isFeatureEnabledonAccount'](
    accountId,
    FEATURE_FLAGS.INTERNAL_CHAT
  ) &&
  userCanReadResource('internalChat', rootGetters.getCurrentUser, accountId);

// CRIAR grupo (#1749): InternalChat::Access.can_manage? no servidor. O administrador passa
// sempre; os demais, com `internal_chat_manage` (já expandido pelas implicações em
// getUserPermissions). Gerenciar um grupo que já existe é do administrador DO GRUPO (#1764) —
// `admin_ids` do canal.
export const canManageInternalChatGroups = (rootGetters, accountId) =>
  isInternalChatAvailable(rootGetters, accountId) &&
  hasPermissions(
    ['administrator', 'internal_chat_manage'],
    rootGetters.getCurrentUser?.accounts
      ? getUserPermissions(rootGetters.getCurrentUser, accountId)
      : []
  );

// As regras do grupo (#1764), só as três conhecidas e sempre booleanas. Canal direto não tem
// regra: `null`.
export const GROUP_SETTINGS = [
  'only_admins_send',
  'only_admins_edit_info',
  'only_admins_add_members',
];
const pickSettings = settings =>
  settings
    ? Object.fromEntries(GROUP_SETTINGS.map(key => [key, !!settings[key]]))
    : null;

// O canal ainda vale para esta pessoa? Grupo desfeito, ou sem ela entre os membros, não.
const isGoneFor = (channel, userId) =>
  !!channel.archived_at || !(channel.member_ids || []).includes(userId);

const pickAttachment = attachment => ({
  id: attachment.id,
  file_type: attachment.file_type || 'file',
  content_type: attachment.content_type || null,
  file_name: attachment.file_name || '',
  file_size: toId(attachment.file_size),
  width: attachment.width || null,
  height: attachment.height || null,
  url: attachment.url || null,
  download_url: attachment.download_url || null,
  thumb_url: attachment.thumb_url || null,
  urls_expire_at: attachment.urls_expire_at || null,
});

// A citação de uma resposta (#1775), como o servidor a mandou (`reply_to`). Apagada: sem texto e
// sem menção. Nunca tem link de arquivo — só quantos e de que tipo é o primeiro.
export const pickQuote = quote => ({
  id: quote.id,
  sender_id: quote.sender_id || null,
  sender: quote.sender || null,
  created_at: quote.created_at,
  revision: toId(quote.revision),
  deleted: !!quote.deleted,
  content: quote.deleted ? null : quote.content || '',
  mentions: quote.deleted ? [] : quote.mentions || [],
  mentions_pending: !quote.deleted && !!quote.mentions_pending,
  attachments_count: quote.deleted ? 0 : toId(quote.attachments_count),
  file_type: quote.deleted ? null : quote.file_type || null,
});

// A citação tirada da própria mensagem (já passada por `pickMessage`): a prévia "respondendo a"
// do compositor e a atualização ao vivo das respostas quando a original muda.
export const quoteFrom = message =>
  pickQuote({
    id: message.id,
    sender_id: message.sender_id,
    sender: message.sender,
    created_at: message.created_at,
    revision: message.revision,
    deleted: !!message.deleted_at,
    content: message.content,
    mentions: message.mentions,
    mentions_pending: message.mentions_pending,
    attachments_count: (message.attachments || []).length,
    file_type: (message.attachments || [])[0]?.file_type || null,
  });

// Mensagem apagada fica sem conteúdo também aqui, qualquer que seja o payload — e sem arquivo.
const pickMessage = payload => ({
  id: payload.id,
  channel_id: payload.channel_id,
  account_id: payload.account_id,
  sender_id: payload.sender_id,
  sender: payload.sender || null,
  content: payload.deleted_at ? null : payload.content,
  created_at: payload.created_at,
  edited_at: payload.edited_at || null,
  deleted_at: payload.deleted_at || null,
  revision: toId(payload.revision),
  // Quem o texto cita, entre os membros (#1784) — só no evento do socket; a resposta HTTP diz o
  // mesmo pelas menções resolvidas.
  mentioned_user_ids: payload.deleted_at
    ? []
    : (payload.mentioned_user_ids || []).map(toId).filter(Boolean),
  attachments: payload.deleted_at
    ? []
    : (payload.attachments || []).map(pickAttachment),
  // Menções resolvidas para quem lê (#1750); a apagada não tem nenhuma.
  mentions: payload.deleted_at ? [] : payload.mentions || [],
  mentions_pending: !payload.deleted_at && !!payload.mentions_pending,
  // Responder e encaminhar (#1775).
  forwarded: !!payload.forwarded,
  reply_to_id: payload.reply_to_id || null,
  reply_to: payload.reply_to ? pickQuote(payload.reply_to) : null,
});

// A citação de `message` com a mudança conhecida da original aplicada. A pendente marca a
// resposta como pendente também: é o que manda a tela buscá-la pelo HTTP, que resolve.
const withQuoteUpdate = (message, update) => {
  if (!message?.reply_to || !update || message.reply_to.id !== update.id) {
    return message;
  }
  const quote = newerQuote(message.reply_to, quoteFrom(update));
  if (quote === message.reply_to) return message;
  return {
    ...message,
    reply_to: quote,
    mentions_pending: message.mentions_pending || quote.mentions_pending,
  };
};

const logUnlessCanceled = (label, error) => {
  if (error?.code === 'ERR_CANCELED') return;
  // eslint-disable-next-line no-console
  console.error(`[internalChat/${label}]`, error);
};

// A conta de um canal do store (#1752): é com ela que vai todo pedido sobre o canal. Canal
// desconhecido não tem conta, e o pedido não sai — nunca cai para a conta da URL, que mandaria a
// resposta de uma conversa da conta B para a conta A.
const accountOf = ($state, channelId) =>
  $state.channels[channelId]?.account_id || null;

const requireAccountOf = ($state, channelId) => {
  const accountId = accountOf($state, channelId);
  if (!accountId) {
    throw new Error(`[internalChat] canal ${channelId} fora do store`);
  }
  return accountId;
};

// 401 (sem vínculo, conta ou franquia suspensa) e 403 (sem a flag ou a permissão): a conta saiu
// do alcance da pessoa desde a última lista.
const isDenied = error => [401, 403].includes(error?.response?.status);

const sumUnread = ($state, channels, userId) =>
  channels.reduce(
    (sum, channel) =>
      sum +
      unreadOf(
        channel,
        $state.messages[channel.id],
        userId,
        $state.updates[channel.id]
      ),
    0
  );

export const getters = {
  getUIFlags: $state => $state.uiFlags,
  isAccountLoaded: $state => accountId => !!$state.loadedAccounts[accountId],
  isAccountsLoaded: $state => $state.accountsLoaded,
  getAccounts: $state => $state.accounts,
  // Outra conta que o servidor listou na visão consolidada (#1752). A lista branca do socket
  // (actionCable.js) pergunta só isto — nunca "qualquer conta do usuário".
  isCrossAccountEligible: $state => accountId =>
    !!accountId && $state.accounts.some(account => account.id === accountId),
  // A conta da URL, ou uma da lista do servidor.
  isKnownAccount: (_state, local, _rootState, rootGetters) => accountId =>
    !!accountId &&
    (accountId === rootGetters.getCurrentAccountId ||
      local.isCrossAccountEligible(accountId)),
  knownAccountIds: ($state, _local, _rootState, rootGetters) => {
    const ids = $state.accounts.map(account => account.id);
    const home = rootGetters.getCurrentAccountId;
    return home && !ids.includes(home) ? [home, ...ids] : ids;
  },
  getPeople: $state => accountId => $state.people[accountId] || [],
  getActiveChannelId: $state => $state.activeChannelId,
  getChannel: $state => id => $state.channels[id] || null,
  getMessages: $state => channelId => $state.messages[channelId] || [],
  // A mensagem que o compositor do canal está respondendo (#1775), ou null.
  getReplyingTo: $state => channelId => $state.replyingTo[channelId] || null,
  hasMoreMessages: $state => channelId => !!$state.hasMore[channelId],
  isHistoryLoaded: $state => channelId => !!$state.historyLoaded[channelId],
  getChannels: $state =>
    Object.values($state.channels).sort(
      (a, b) =>
        lastKnownId(b, $state.messages[b.id]) -
        lastKnownId(a, $state.messages[a.id])
    ),
  // Os grupos, do que teve mensagem mais recente para o mais antigo.
  getGroups: (_state, local) =>
    local.getChannels.filter(channel => channel.kind === 'group'),
  getLastMessage: $state => channelId => {
    const messages = $state.messages[channelId] || [];
    const local = messages[messages.length - 1];
    const fromServer = $state.channels[channelId]?.last_message;
    if (!local) return fromServer || null;
    if (!fromServer) return local;
    return local.id >= fromServer.id ? local : fromServer;
  },
  // Canal direto com `userId` NA CONTA `accountId` (a da URL, se não vier). Nunca consigo mesmo:
  // todo canal do store tem a pessoa logada. A mesma dupla tem um canal por conta (#1752).
  getChannelByUserId:
    ($state, _getters, _rootState, rootGetters) =>
    (userId, accountId = rootGetters.getCurrentAccountId) => {
      const me = rootGetters.getCurrentUserID;
      if (!userId || userId === me) return null;
      return (
        Object.values($state.channels).find(
          channel =>
            channel.kind === 'direct' &&
            channel.account_id === accountId &&
            (channel.member_ids || []).includes(userId)
        ) || null
      );
    },
  getUnreadCountByChannel:
    ($state, _getters, _rootState, rootGetters) => channelId =>
      unreadOf(
        $state.channels[channelId],
        $state.messages[channelId],
        rootGetters.getCurrentUserID,
        $state.updates[channelId]
      ),
  // O "@" ao lado do contador do canal (#1784).
  hasUnreadMentionByChannel:
    ($state, _getters, _rootState, rootGetters) => channelId =>
      hasUnreadMentionOf(
        $state.channels[channelId],
        $state.messages[channelId],
        rootGetters.getCurrentUserID,
        $state.updates[channelId]
      ),
  // Algum canal conhecido, de qualquer conta carregada, com menção não lida — o "@" da sidebar.
  hasUnreadMention: ($state, local) =>
    Object.keys($state.channels).some(id =>
      local.hasUnreadMentionByChannel(Number(id))
    ),
  // Não lidas de uma conta (#1752). Carregada: a soma dos canais dela, cada um com a sua foto.
  // Ainda não carregada: o número do agregado — ou o que o socket já trouxe, se for maior. O
  // maior, e não a soma: os dois podem contar a mesma mensagem, e cada um sozinho nunca conta a
  // mais. A diferença dura até a lista da conta chegar (ela é pedida no primeiro evento).
  getUnreadCountByAccount:
    ($state, _getters, _rootState, rootGetters) => accountId => {
      const fromChannels = sumUnread(
        $state,
        Object.values($state.channels).filter(
          channel => channel.account_id === accountId
        ),
        rootGetters.getCurrentUserID
      );
      if ($state.loadedAccounts[accountId]) return fromChannels;
      const summary = $state.accounts.find(account => account.id === accountId);
      return Math.max(fromChannels, toId(summary?.unread_count));
    },
  // O badge da sidebar: a soma das contas conhecidas — a da URL e as da visão consolidada.
  getUnreadCount: (_state, local) =>
    local.knownAccountIds.reduce(
      (sum, accountId) => sum + local.getUnreadCountByAccount(accountId),
      0
    ),
  // A parte do badge que é de OUTRAS contas (o tooltip da sidebar separa as duas).
  getOtherAccountsUnreadCount: (_state, local, _rootState, rootGetters) =>
    local.getUnreadCount -
    local.getUnreadCountByAccount(rootGetters.getCurrentAccountId),
};

export const actions = {
  // Carga do boot (sidebar) e da tela, por conta. Conta da URL sem a flag ou sem a permissão: nem
  // pergunta ao servidor (a resposta seria 403), e o store fica vazio — badge zero, e sem visão
  // consolidada. OUTRA conta só se o servidor a listou (#1752); a resposta de uma conta que saiu
  // da lista enquanto o pedido voava é descartada.
  fetchChannels: async (
    { commit, getters: local, rootGetters },
    { accountId } = {}
  ) => {
    const home = rootGetters.getCurrentAccountId;
    const id = accountId || home;
    if (id === home && !isInternalChatAvailable(rootGetters, id)) {
      commit(types.RESET);
      return;
    }
    if (!local.isKnownAccount(id)) return;
    commit(types.SET_UI_FLAG, { isFetchingChannels: true });
    try {
      const { data } = await InternalChatAPI.getChannels(id);
      if (!local.isKnownAccount(id)) return;
      commit(types.SET_CHANNELS, { accountId: id, channels: data.payload });
    } catch (error) {
      if (id !== home && isDenied(error)) commit(types.REMOVE_ACCOUNT, id);
      logUnlessCanceled('fetchChannels', error);
    } finally {
      commit(types.SET_UI_FLAG, { isFetchingChannels: false });
    }
  },

  // A lista da visão consolidada (#1752). Só existe com o chat disponível na conta da URL: é de
  // dentro dela que as outras são vistas. Conta que saiu da lista leva o que era dela no store;
  // conta com não lidas tem a lista de canais carregada já, porque o badge precisa da foto de
  // cada canal para não contar a mesma mensagem duas vezes.
  fetchAccounts: async ({ commit, dispatch, state: $state, rootGetters }) => {
    const home = rootGetters.getCurrentAccountId;
    if (!isInternalChatAvailable(rootGetters, home)) return;
    accountsRequest += 1;
    const request = accountsRequest;
    try {
      const { data } = await InternalChatAPI.getAccounts();
      if (request !== accountsRequest) return;
      commit(types.SET_ACCOUNTS, { home, accounts: data.payload || [] });
      $state.accounts
        .filter(
          account =>
            account.id !== home &&
            account.unread_count > 0 &&
            !$state.loadedAccounts[account.id]
        )
        .forEach(account =>
          dispatch('ensureAccountLoaded', { accountId: account.id })
        );
    } catch (error) {
      logUnlessCanceled('fetchAccounts', error);
    }
  },

  // A lista de canais de uma conta, uma vez: a rajada de eventos de uma conta ainda não
  // carregada vira um pedido só.
  ensureAccountLoaded: ({ dispatch, state: $state }, { accountId }) => {
    if (!accountId || $state.loadedAccounts[accountId]) return null;
    if (loadingAccounts.has(accountId)) return loadingAccounts.get(accountId);
    const request = dispatch('fetchChannels', { accountId }).finally(() =>
      loadingAccounts.delete(accountId)
    );
    loadingAccounts.set(accountId, request);
    return request;
  },

  // As pessoas de OUTRA conta (#1752). A da URL vem do store `agents`.
  fetchPeople: async (
    { commit, getters: local, rootGetters },
    { accountId }
  ) => {
    if (accountId === rootGetters.getCurrentAccountId) return;
    if (!local.isCrossAccountEligible(accountId)) return;
    try {
      const { data } = await InternalChatAPI.getPeople(accountId);
      if (!local.isCrossAccountEligible(accountId)) return;
      commit(types.SET_PEOPLE, { accountId, people: data.payload || [] });
    } catch (error) {
      if (isDenied(error)) commit(types.REMOVE_ACCOUNT, accountId);
      logUnlessCanceled('fetchPeople', error);
    }
  },

  // Abre (ou reusa) o canal direto com `userId` na conta `accountId` (a da URL, se não vier).
  // Erro sobe: a tela mostra o aviso.
  openChannel: async (
    { commit, getters: local, rootGetters, state: $state },
    { userId, accountId: requested }
  ) => {
    const accountId = requested || rootGetters.getCurrentAccountId;
    if (!local.isKnownAccount(accountId)) return null;
    const existing = local.getChannelByUserId(userId, accountId);
    if (existing?.snapshot) return existing;

    const { data } = await InternalChatAPI.openDirectChannel(accountId, userId);
    commit(types.UPSERT_CHANNEL, { accountId, channel: data });
    return $state.channels[data.id];
  },

  fetchMessages: async ({ commit, state: $state }, { channelId, before }) => {
    const accountId = requireAccountOf($state, channelId);
    commit(types.SET_UI_FLAG, { isFetchingMessages: true });
    try {
      const { data } = await InternalChatAPI.getMessages(accountId, channelId, {
        before,
      });
      commit(types.ADD_MESSAGES, { channelId, messages: data.payload });
      commit(types.SET_HAS_MORE, { channelId, hasMore: data.meta.has_more });
      commit(types.SET_HISTORY_LOADED, channelId);
    } finally {
      commit(types.SET_UI_FLAG, { isFetchingMessages: false });
    }
  },

  // Depois de uma queda do socket: o que chegou enquanto a aba estava surda, do último id
  // conhecido em diante.
  fetchNewerMessages: async ({ commit, state: $state }, { channelId }) => {
    const accountId = requireAccountOf($state, channelId);
    let page = 0;
    let hasMore = true;
    while (hasMore && page < MAX_CATCH_UP_PAGES) {
      const after = maxId($state.messages[channelId] || []);
      // eslint-disable-next-line no-await-in-loop
      const { data } = await InternalChatAPI.getMessages(accountId, channelId, {
        after,
      });
      commit(types.ADD_MESSAGES, { channelId, messages: data.payload });
      hasMore = data.meta.has_more && data.payload.length > 0;
      page += 1;
    }
  },

  // Texto, arquivos (#1756) ou os dois. Com arquivo, o andamento vai para `uploadProgress` e o
  // envio pode ser cancelado (`cancelUpload`). Erro sobe — inclusive o cancelamento
  // (`ERR_CANCELED`) —, e a tela decide o aviso e mantém o que estava no compositor.
  //
  // A conta do envio é a do CANAL (#1752): responder numa conversa da conta B com a conta A na
  // URL entrega na B.
  //
  // Resposta (#1775): `replyToId`, ou — sem ele — a mensagem em `replyingTo` daquele canal. O
  // estado "respondendo a" só sai quando o envio dá certo: com erro, a citação fica no compositor
  // junto com o texto.
  sendMessage: async (
    { commit, state: $state },
    { channelId, content, files = [], replyToId }
  ) => {
    const accountId = requireAccountOf($state, channelId);
    const replyTo =
      replyToId === undefined ? $state.replyingTo[channelId]?.id : replyToId;
    const controller = files.length ? new AbortController() : null;
    pendingUpload = controller;
    commit(types.SET_UI_FLAG, {
      isSending: true,
      uploadProgress: controller ? 0 : null,
    });
    try {
      const { data } = controller
        ? await InternalChatAPI.sendMessage(accountId, channelId, content, {
            files,
            ...(replyTo ? { replyToId: replyTo } : {}),
            signal: controller.signal,
            onUploadProgress: event => {
              if (!event?.total) return;
              commit(types.SET_UI_FLAG, {
                uploadProgress: Math.round((event.loaded * 100) / event.total),
              });
            },
          })
        : await InternalChatAPI.sendMessage(
            accountId,
            channelId,
            content,
            ...(replyTo ? [{ replyToId: replyTo }] : [])
          );
      commit(types.ADD_MESSAGES, { channelId, messages: [pickMessage(data)] });
      // O servidor avança o marcador de quem escreve até a própria mensagem.
      commit(types.SET_LOCAL_READ, { channelId, messageId: data.id });
      if (replyTo && $state.replyingTo[channelId]?.id === replyTo) {
        commit(types.SET_REPLYING_TO, { channelId, quote: null });
      }
      return data;
    } finally {
      if (pendingUpload === controller) pendingUpload = null;
      commit(types.SET_UI_FLAG, { isSending: false, uploadProgress: null });
    }
  },

  cancelUpload: () => {
    if (pendingUpload) pendingUpload.abort();
  },

  // "Responder" (#1775): a mensagem carregada do canal vira a citação do compositor. Apagada não
  // se responde por aqui — não há o que citar.
  startReply: ({ commit, state: $state }, { channelId, messageId }) => {
    const message = ($state.messages[channelId] || []).find(
      item => item.id === messageId
    );
    if (!message || message.deleted_at) return;
    commit(types.SET_REPLYING_TO, { channelId, quote: quoteFrom(message) });
  },

  cancelReply: ({ commit }, { channelId }) => {
    commit(types.SET_REPLYING_TO, { channelId, quote: null });
  },

  // Encaminhar (#1775) para grupos (`channelIds`) e pessoas (`userIds`) da conta do canal de
  // origem — o servidor recusa destino de outra conta. Devolve o resultado de cada destino
  // (`forwarded` ou `failed` com `reason`/`error`); as cópias chegam pelo socket, como mensagem
  // nova de cada canal (inclusive a conversa direta que o servidor acabou de abrir). Erro do
  // pedido inteiro (sem destino, destinos demais, mensagem apagada) sobe.
  forwardMessage: async (
    { state: $state },
    { channelId, messageId, channelIds = [], userIds = [] }
  ) => {
    const { data } = await InternalChatAPI.forwardMessage(
      requireAccountOf($state, channelId),
      channelId,
      messageId,
      { channelIds, userIds }
    );
    return data.payload || [];
  },

  // Links novos para os arquivos de uma mensagem (#1756): a que chegou pelo socket (sem link) ou
  // cujos links venceram. Um pedido por mensagem de cada vez. Erro não sobe: quem saiu do canal
  // recebe 404 e o balão fica como estava.
  refreshMessage: ({ commit, state: $state }, { channelId, messageId }) => {
    const accountId = accountOf($state, channelId);
    if (!accountId) return Promise.resolve();
    const key = `${channelId}:${messageId}`;
    if (refreshing.has(key)) return refreshing.get(key);
    const request = InternalChatAPI.getMessage(accountId, channelId, messageId)
      .then(({ data }) => {
        commit(types.ADD_MESSAGES, {
          channelId,
          messages: [pickMessage(data)],
        });
      })
      .catch(error => logUnlessCanceled('refreshMessage', error))
      .finally(() => refreshing.delete(key));
    refreshing.set(key, request);
    return request;
  },

  // Marca como lido até a última mensagem conhecida. Otimista: o badge cai na hora, e a foto
  // que o servidor devolve assume. Se o servidor recusar, desfaz — a não ser que uma leitura
  // mais nova já tenha passado por cima.
  markRead: async (
    { commit, getters: local, state: $state },
    { channelId }
  ) => {
    const channel = $state.channels[channelId];
    if (!channel || local.getUnreadCountByChannel(channelId) === 0) return;

    const messageId = lastKnownId(channel, $state.messages[channelId]);
    const previous = channel.local_read_id || 0;
    commit(types.SET_LOCAL_READ, { channelId, messageId });
    try {
      const { data } = await InternalChatAPI.markRead(
        channel.account_id,
        channelId,
        messageId
      );
      commit(types.APPLY_SNAPSHOT, { channelId, snapshot: data });
    } catch (error) {
      commit(types.UNDO_LOCAL_READ, { channelId, messageId, previous });
      logUnlessCanceled('markRead', error);
    }
  },

  // Editar e apagar a própria mensagem (#1757). Erro sobe: a tela mostra o aviso que o servidor
  // mandou (403 de quem não é o autor, 422 de mensagem já apagada ou canal arquivado). Sem
  // otimismo: a versão que vale é a que o servidor gravou, e o evento das outras abas é o mesmo.
  editMessage: async (
    { commit, state: $state },
    { channelId, messageId, content }
  ) => {
    const { data } = await InternalChatAPI.updateMessage(
      requireAccountOf($state, channelId),
      channelId,
      messageId,
      content
    );
    commit(types.APPLY_MESSAGE_UPDATE, {
      channelId,
      message: pickMessage(data),
    });
    return data;
  },

  deleteMessage: async (
    { commit, state: $state },
    { channelId, messageId }
  ) => {
    const { data } = await InternalChatAPI.deleteMessage(
      requireAccountOf($state, channelId),
      channelId,
      messageId
    );
    commit(types.APPLY_MESSAGE_UPDATE, {
      channelId,
      message: pickMessage(data),
    });
    return data;
  },

  // `internal_chat.message_updated` (#1757). Guardado mesmo quando o canal ou a mensagem ainda
  // não estão no store: a carga da lista em voo, ou o `message_created` atrasado, chegam depois
  // e encontram a mudança esperando.
  receiveMessageUpdated: (
    { commit, getters: local, state: $state },
    payload
  ) => {
    if (!payload?.id || !payload.channel_id) return;
    if (!local.isKnownAccount(payload.account_id)) return;
    if ($state.removedChannelIds[payload.channel_id]) return;

    commit(types.APPLY_MESSAGE_UPDATE, {
      channelId: payload.channel_id,
      message: pickMessage(payload),
    });
  },

  // Grupos (#1749). Erro sobe: a tela mostra o aviso que o servidor mandou. Grupo novo nasce na
  // conta `accountId` (a da URL, se não vier).
  createGroup: async (
    { commit, rootGetters, state: $state },
    { name, memberIds, accountId: requested }
  ) => {
    const accountId = requested || rootGetters.getCurrentAccountId;
    commit(types.SET_UI_FLAG, { isSavingGroup: true });
    try {
      const { data } = await InternalChatAPI.createGroup(accountId, {
        name,
        memberIds,
      });
      commit(types.UPSERT_CHANNEL, { accountId, channel: data });
      return $state.channels[data.id];
    } finally {
      commit(types.SET_UI_FLAG, { isSavingGroup: false });
    }
  },

  addGroupMembers: async (
    { commit, state: $state },
    { channelId, userIds }
  ) => {
    commit(types.SET_UI_FLAG, { isSavingGroup: true });
    try {
      const accountId = requireAccountOf($state, channelId);
      const { data } = await InternalChatAPI.addGroupMembers(
        accountId,
        channelId,
        userIds
      );
      commit(types.UPSERT_CHANNEL, { accountId, channel: data });
    } finally {
      commit(types.SET_UI_FLAG, { isSavingGroup: false });
    }
  },

  removeGroupMember: async (
    { commit, state: $state },
    { channelId, userId }
  ) => {
    commit(types.SET_UI_FLAG, { isSavingGroup: true });
    try {
      const accountId = requireAccountOf($state, channelId);
      const { data } = await InternalChatAPI.removeGroupMember(
        accountId,
        channelId,
        userId
      );
      commit(types.UPSERT_CHANNEL, { accountId, channel: data });
    } finally {
      commit(types.SET_UI_FLAG, { isSavingGroup: false });
    }
  },

  // Promover/rebaixar (#1764). A resposta é o grupo como ficou.
  setGroupMemberRole: async (
    { commit, state: $state },
    { channelId, userId, role }
  ) => {
    commit(types.SET_UI_FLAG, { isSavingGroup: true });
    try {
      const accountId = requireAccountOf($state, channelId);
      const { data } = await InternalChatAPI.setGroupMemberRole(
        accountId,
        channelId,
        userId,
        role
      );
      commit(types.UPSERT_CHANNEL, { accountId, channel: data });
    } finally {
      commit(types.SET_UI_FLAG, { isSavingGroup: false });
    }
  },

  // Nome e/ou regras (#1764).
  updateGroup: async (
    { commit, state: $state },
    { channelId, name, settings }
  ) => {
    commit(types.SET_UI_FLAG, { isSavingGroup: true });
    try {
      const accountId = requireAccountOf($state, channelId);
      const { data } = await InternalChatAPI.updateGroup(accountId, channelId, {
        name,
        settings,
      });
      commit(types.UPSERT_CHANNEL, { accountId, channel: data });
    } finally {
      commit(types.SET_UI_FLAG, { isSavingGroup: false });
    }
  },

  // Sair (#1764). Como desfazer: o grupo sai do store na hora; as outras abas o perdem pelo
  // evento, que vai também para quem saiu.
  leaveGroup: async ({ commit, state: $state }, { channelId }) => {
    commit(types.SET_UI_FLAG, { isSavingGroup: true });
    try {
      await InternalChatAPI.leaveGroup(
        requireAccountOf($state, channelId),
        channelId
      );
      commit(types.REMOVE_CHANNEL, channelId);
    } finally {
      commit(types.SET_UI_FLAG, { isSavingGroup: false });
    }
  },

  // Quem pode entrar num grupo da conta (#1764): a mesma lista do servidor que alimenta as
  // pessoas das outras contas (`GET /internal_chat/users`, só quem alcança o chat). Devolve os
  // ids, sem guardar: a lista de pessoas da conta da URL continua vindo do store `agents`. Na
  // falha, `null` — a tela oferece todo mundo e o servidor recusa quem não pode (422).
  fetchGroupCandidateIds: async (_context, { accountId }) => {
    try {
      const { data } = await InternalChatAPI.getPeople(accountId);
      return (data.payload || []).map(person => person.id);
    } catch (error) {
      logUnlessCanceled('fetchGroupCandidateIds', error);
      return null;
    }
  },

  // Desfazer = arquivar. O grupo sai do store na hora; os outros membros o perdem pelo evento.
  archiveGroup: async ({ commit, state: $state }, { channelId }) => {
    commit(types.SET_UI_FLAG, { isSavingGroup: true });
    try {
      await InternalChatAPI.archiveGroup(
        requireAccountOf($state, channelId),
        channelId
      );
      commit(types.REMOVE_CHANNEL, channelId);
    } finally {
      commit(types.SET_UI_FLAG, { isSavingGroup: false });
    }
  },

  // `internal_chat.channel_updated` (#1749): grupo criado, membro que entrou ou saiu, grupo
  // desfeito. Quem saiu (ou viu o grupo ser desfeito) perde o canal; quem entrou ganha um
  // esboço, que a próxima foto completa.
  receiveChannelUpdated: ({ commit, getters: local, rootGetters }, payload) => {
    if (!payload?.id) return;
    if (!local.isKnownAccount(payload.account_id)) return;

    if (isGoneFor(payload, rootGetters.getCurrentUserID)) {
      commit(types.REMOVE_CHANNEL, payload.id);
      return;
    }
    commit(types.APPLY_CHANNEL_INFO, payload);
  },

  setActiveChannel: ({ commit }, channelId) => {
    commit(types.SET_ACTIVE_CHANNEL, channelId || null);
  },

  // `internal_chat.message_created`. Canal que o store ainda não conhece (a carga do boot não
  // voltou, ou o colega acabou de abrir a conversa) entra como esboço e conta só o que o socket
  // trouxer; a próxima foto completa o resto.
  //
  // Conta que não é a da URL nem está na lista do servidor: descartado (#1752). Conta da lista
  // ainda não carregada: a mensagem entra, e a lista da conta é pedida — é ela que traz a foto
  // que o badge precisa.
  receiveMessage: (
    { commit, dispatch, getters: local, state: $state, rootGetters },
    payload
  ) => {
    if (!payload?.id || !payload.channel) return;
    if (!local.isKnownAccount(payload.account_id)) return;

    const channelId = payload.channel_id;
    if ($state.removedChannelIds[channelId]) return;
    commit(types.ADD_CHANNEL_STUB, {
      ...payload.channel,
      account_id: payload.account_id,
    });
    commit(types.ADD_MESSAGES, { channelId, messages: [pickMessage(payload)] });
    if (payload.sender_id === rootGetters.getCurrentUserID) {
      commit(types.SET_LOCAL_READ, { channelId, messageId: payload.id });
    }
    if (payload.account_id !== rootGetters.getCurrentAccountId) {
      dispatch('ensureAccountLoaded', { accountId: payload.account_id });
    }
  },

  // `internal_chat.channel_read`: a mesma pessoa leu em outra aba. A foto vem junto.
  receiveChannelRead: ({ commit, rootGetters }, payload) => {
    if (!payload || payload.user_id !== rootGetters.getCurrentUserID) return;
    commit(types.APPLY_SNAPSHOT, {
      channelId: payload.channel_id,
      snapshot: payload,
    });
  },

  // Depois da queda: a lista do servidor primeiro (uma conta pode ter saído ou entrado), e então
  // a de cada conta que já estava carregada e continua na lista.
  syncOnReconnect: async ({ dispatch, getters: local, state: $state }) => {
    const loaded = Object.keys($state.loadedAccounts).map(Number);
    if (!loaded.length) return;
    await dispatch('fetchAccounts');
    await Promise.all(
      loaded
        .filter(accountId => local.isKnownAccount(accountId))
        .map(accountId => dispatch('fetchChannels', { accountId }))
    );
    const channelId = $state.activeChannelId;
    if (channelId && $state.historyLoaded[channelId]) {
      try {
        await dispatch('fetchNewerMessages', { channelId });
      } catch (error) {
        logUnlessCanceled('syncOnReconnect', error);
      }
    }
  },
};

const applySnapshot = (channel, incoming) => {
  const snapshot = snapshotOf(incoming);
  if (!isNotOlderSnapshot(channel.snapshot, snapshot)) return false;
  channel.snapshot = snapshot;
  return true;
};

// A última mensagem anda junto com a foto: resposta velha que chega atrasada não pode trocar
// a prévia da lista por uma mensagem anterior à que já está lá.
const upsertChannel = ($state, accountId, serverChannel) => {
  const current = $state.channels[serverChannel.id];
  const next = {
    local_read_id: 0,
    last_message: null,
    ...current,
    id: serverChannel.id,
    account_id: serverChannel.account_id ?? accountId,
    kind: serverChannel.kind,
    name: serverChannel.name,
    archived_at: serverChannel.archived_at || null,
    member_ids: serverChannel.member_ids || [],
    admin_ids: serverChannel.admin_ids || [],
    settings: pickSettings(serverChannel.settings),
    counterpart: serverChannel.counterpart || current?.counterpart || null,
  };
  if (applySnapshot(next, serverChannel)) {
    const lastMessage = serverChannel.last_message
      ? pickMessage(serverChannel.last_message)
      : null;
    next.last_message = lastMessage
      ? newerVersion(
          $state.updates[serverChannel.id]?.[lastMessage.id],
          lastMessage
        )
      : null;
  }
  Vue.set($state.channels, serverChannel.id, next);
  Vue.delete($state.removedChannelIds, serverChannel.id);
};

// Tudo o que o store guarda de uma conta.
const purgeAccount = ($state, accountId) => {
  Object.values($state.channels).forEach(channel => {
    if (channel.account_id !== accountId) return;
    Vue.delete($state.channels, channel.id);
    Vue.delete($state.messages, channel.id);
    Vue.delete($state.updates, channel.id);
    Vue.delete($state.hasMore, channel.id);
    Vue.delete($state.historyLoaded, channel.id);
    if ($state.activeChannelId === channel.id) $state.activeChannelId = null;
  });
  Vue.delete($state.loadedAccounts, accountId);
  Vue.delete($state.people, accountId);
};

// O que a lista de pessoas de outra conta desenha (#1752) — e só isso.
const pickPerson = person => ({
  id: person.id,
  name: person.name,
  available_name: person.available_name || null,
  thumbnail: person.thumbnail || '',
  availability_status: person.availability_status || 'offline',
});

const stubFrom = channel => ({
  id: channel.id,
  account_id: channel.account_id,
  kind: channel.kind,
  name: channel.name || null,
  archived_at: null,
  member_ids: channel.member_ids || [],
  admin_ids: channel.admin_ids || [],
  settings: pickSettings(channel.settings),
  counterpart: null,
  last_message: null,
  snapshot: null,
  local_read_id: 0,
});

export const mutations = {
  [types.RESET]($state) {
    Object.assign($state, blankState());
  },
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  // Lista completa de UMA conta (#1752: as outras contas do store ficam como estão). Canal dela
  // que tinha foto e saiu da lista (arquivado, ou o outro lado perdeu o acesso) sai também;
  // esboço vindo do socket fica — ele pode ser mais novo que esta resposta.
  [types.SET_CHANNELS]($state, { accountId, channels }) {
    Vue.set($state.loadedAccounts, accountId, true);
    const listed = new Set(channels.map(channel => channel.id));
    Object.values($state.channels).forEach(channel => {
      if (
        channel.account_id === accountId &&
        channel.snapshot &&
        !listed.has(channel.id)
      ) {
        Vue.delete($state.channels, channel.id);
      }
    });
    channels.forEach(channel => upsertChannel($state, accountId, channel));
  },
  [types.UPSERT_CHANNEL]($state, { accountId, channel }) {
    upsertChannel($state, accountId, channel);
  },
  // A lista da visão consolidada (#1752). Conta que estava e saiu — vínculo, flag, permissão,
  // conta ou franquia suspensa — leva junto tudo o que era dela: canais, mensagens, pessoas.
  // A conta da URL não sai por aqui: quem decide sobre ela é a flag e a permissão da própria
  // conta (`fetchChannels`).
  [types.SET_ACCOUNTS]($state, { home, accounts }) {
    const kept = new Set(accounts.map(account => account.id));
    $state.accounts = accounts.map(account => ({
      id: account.id,
      name: account.name,
      unread_count: toId(account.unread_count),
      mention_count: toId(account.mention_count),
    }));
    $state.accountsLoaded = true;
    const stale = new Set(
      Object.values($state.channels).map(channel => channel.account_id)
    );
    Object.keys($state.loadedAccounts).forEach(id => stale.add(Number(id)));
    Object.keys($state.people).forEach(id => stale.add(Number(id)));
    stale.forEach(accountId => {
      if (accountId !== home && !kept.has(accountId)) {
        purgeAccount($state, accountId);
      }
    });
  },
  // Uma conta que recusou um pedido (401/403) sai já, sem esperar a próxima lista.
  [types.REMOVE_ACCOUNT]($state, accountId) {
    $state.accounts = $state.accounts.filter(
      account => account.id !== accountId
    );
    purgeAccount($state, accountId);
  },
  [types.SET_PEOPLE]($state, { accountId, people }) {
    Vue.set($state.people, accountId, people.map(pickPerson));
  },
  [types.ADD_CHANNEL_STUB]($state, channel) {
    if ($state.channels[channel.id]) return;
    Vue.set($state.channels, channel.id, stubFrom(channel));
  },
  // Nome e membros vindos do evento de grupo. Não toca na foto de não lidas: ela é da lista.
  [types.APPLY_CHANNEL_INFO]($state, channel) {
    Vue.delete($state.removedChannelIds, channel.id);
    const current = $state.channels[channel.id];
    if (!current) {
      Vue.set($state.channels, channel.id, stubFrom(channel));
      return;
    }
    current.name = channel.name ?? current.name;
    current.member_ids = channel.member_ids || current.member_ids;
    current.admin_ids = channel.admin_ids || current.admin_ids;
    current.settings = channel.settings
      ? pickSettings(channel.settings)
      : current.settings;
  },
  [types.REMOVE_CHANNEL]($state, channelId) {
    Vue.set($state.removedChannelIds, channelId, true);
    Vue.delete($state.channels, channelId);
    Vue.delete($state.messages, channelId);
    Vue.delete($state.updates, channelId);
    Vue.delete($state.hasMore, channelId);
    Vue.delete($state.historyLoaded, channelId);
    if ($state.activeChannelId === channelId) $state.activeChannelId = null;
  },
  // A mudança conhecida (#1757) vale sobre a cópia que chega: o `message_created` atrasado de uma
  // mensagem já apagada não devolve o texto à tela.
  [types.ADD_MESSAGES]($state, { channelId, messages }) {
    const byId = new Map(
      ($state.messages[channelId] || []).map(message => [message.id, message])
    );
    const updates = $state.updates[channelId] || {};
    messages.forEach(incoming => {
      const message = pickMessage(incoming);
      const known = newerVersion(byId.get(message.id), updates[message.id]);
      const merged = newerVersion(known, message);
      // A original citada já mudou depois da cópia que chegou (#1775).
      byId.set(
        message.id,
        withQuoteUpdate(merged, updates[merged.reply_to?.id])
      );
    });
    const merged = [...byId.values()].sort((a, b) => a.id - b.id);
    Vue.set($state.messages, channelId, merged);
  },
  // Edição ou exclusão (#1757): guarda a mudança e a aplica onde a mensagem já estiver — no
  // histórico carregado e na prévia da lista.
  [types.APPLY_MESSAGE_UPDATE]($state, { channelId, message }) {
    const known = $state.updates[channelId] || {};
    const update = newerVersion(known[message.id], message);
    Vue.set($state.updates, channelId, { ...known, [message.id]: update });

    const list = $state.messages[channelId];
    const index = list ? list.findIndex(item => item.id === message.id) : -1;
    if (index >= 0) {
      Vue.set(list, index, newerVersion(list[index], update));
    }

    const channel = $state.channels[channelId];
    if (channel?.last_message?.id === message.id) {
      channel.last_message = newerVersion(channel.last_message, update);
    }

    // As respostas que citam esta mensagem (#1775) — a citação é lida, não copiada — e a prévia
    // "respondendo a" do compositor.
    (list || []).forEach((item, position) => {
      const next = withQuoteUpdate(item, update);
      if (next !== item) Vue.set(list, position, next);
    });
    if (channel?.last_message) {
      channel.last_message = withQuoteUpdate(channel.last_message, update);
    }
    const replying = $state.replyingTo[channelId];
    if (replying?.id === message.id) {
      Vue.set(
        $state.replyingTo,
        channelId,
        newerQuote(replying, quoteFrom(update))
      );
    }
  },
  [types.SET_REPLYING_TO]($state, { channelId, quote }) {
    if (quote) Vue.set($state.replyingTo, channelId, quote);
    else Vue.delete($state.replyingTo, channelId);
  },
  [types.SET_HAS_MORE]($state, { channelId, hasMore }) {
    Vue.set($state.hasMore, channelId, !!hasMore);
  },
  [types.SET_HISTORY_LOADED]($state, channelId) {
    Vue.set($state.historyLoaded, channelId, true);
  },
  [types.SET_LOCAL_READ]($state, { channelId, messageId }) {
    const channel = $state.channels[channelId];
    if (!channel) return;
    channel.local_read_id = Math.max(
      channel.local_read_id || 0,
      toId(messageId)
    );
  },
  [types.UNDO_LOCAL_READ]($state, { channelId, messageId, previous }) {
    const channel = $state.channels[channelId];
    if (!channel || channel.local_read_id !== messageId) return;
    channel.local_read_id = previous;
  },
  [types.APPLY_SNAPSHOT]($state, { channelId, snapshot }) {
    const channel = $state.channels[channelId];
    if (!channel) return;
    applySnapshot(channel, snapshot);
  },
  [types.SET_ACTIVE_CHANNEL]($state, channelId) {
    $state.activeChannelId = channelId;
  },
};

// Objeto, e não função: o Vuex daqui é o 2.1, que só aceita estado de módulo como função a
// partir do 2.3. Store de teste monta o módulo com `state: blankState()`.
export const state = blankState();

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
